import { Button, List, ListItem, styled, Typography } from '@mui/material';
import { FindInPageOutlined } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useDisclosure } from 'further-ui/hooks';
import { ADD_BANK_ACCOUNT_WITHOUT_FEED } from 'adminConstants/routes';
import { Column } from 'further-ui/components/Layout';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';

const Icon = styled(FindInPageOutlined)(({ theme }) => ({
  fontSize: '120px',
  color: theme.palette.primary.main,
  opacity: 0.5,
}));

const Paragraph = styled(Typography)({
  maxWidth: '650px',
  textAlign: 'center',
});

const Wrapper = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm, 0),
}));

const BulletList = styled(List)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.lg),
  listStyleType: 'disc',
}));

const Bullet = styled(ListItem)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.grey[900],
  display: 'list-item',
  padding: 0,
}));

export enum NoResultsMode {
  NoResults = 'no-results',
  NoCountrySelected = 'no-country-selected',
}

type Props = {
  mode: NoResultsMode;
};

const NoResults: React.FC<Props> = ({ mode }) => {
  const history = useHistory();
  const addAccountDisclosure = useDisclosure();

  const headingText =
    mode === NoResultsMode.NoResults
      ? 'No results match your search'
      : 'Please select country to find your bank account.';

  return (
    <>
      <Wrapper centered spacing="sm">
        <Icon />
        <Typography variant="h4">{headingText}</Typography>
        {mode === NoResultsMode.NoResults && (
          <>
            <Paragraph variant="body1" color="text.secondary">
              Try checking your spelling or using a different country. If you
              can't find your bank, add an account without a bank feed to
              manually import transactions into Further.
            </Paragraph>
            <Button
              size="large"
              variant="outlined"
              onClick={addAccountDisclosure.onOpen}
            >
              Add without bank feed
            </Button>
          </>
        )}
      </Wrapper>
      <ConfirmDialog
        maxWidth="sm"
        title="Add an account without a bank feed"
        onClose={addAccountDisclosure.onClose}
        open={addAccountDisclosure.isOpen}
        content={
          <>
            <Typography variant="h6">You may want to do this if:</Typography>
            <BulletList>
              <Bullet>Your bank doesn't currently provide a bank feed.</Bullet>
              <Bullet>
                Your bank doesn't provide a bank feed for your account type.
              </Bullet>
              <Bullet>You need a petty cash account.</Bullet>
            </BulletList>
            <Typography variant="body2" color="text.secondary">
              Transactions downloaded from your bank can be manually imported
              into this account.
            </Typography>
          </>
        }
        actions={{
          confirm: {
            onClick: () => history.push(ADD_BANK_ACCOUNT_WITHOUT_FEED),
            label: 'Continue',
          },
          cancel: { onClick: addAccountDisclosure.onClose, label: 'Cancel' },
        }}
      />
    </>
  );
};

export default NoResults;
